// Example service: filepath: /home/boss/workspace/pwa/zgrana/src/app/services/example.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { News } from '../interfaces/news.interface';
import { LoggerService } from './logger.service';
import { GenericResponseWithTotal } from '../interfaces/generic.interface';
import { User, LoginUser, EditUser, UserGroup, UserResponse } from '../interfaces/users.interface';
import { DataProcessingService } from './data-prcessing.service';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { Person } from '../interfaces/persons.interface';


@Injectable({ providedIn: 'root' })
export class UsersService { 
  private get apiUrl(): string {
    return this.loggerService.resolveApiUrl('user');
  }

  public currentUser: User | null = null; // Initialize currentUser as null

  public loggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    !!(localStorage.getItem('authToken') || localStorage.getItem('userToken'))
  ); // Initialize based on any available token

  constructor(
    private http: HttpClient,
    private dataProcess: DataProcessingService,
    private loggerService: LoggerService
  ) {}

  getData(): Observable<GenericResponseWithTotal<User>> {
    return this.http.get<GenericResponseWithTotal<User>>(this.apiUrl);
  }

  getGroup(): Observable<Array<UserGroup>> {
    return this.http.get<Array<UserGroup>>(`${this.apiUrl}/group`);
  }

  getRole(): Observable<string> {
    return this.http.get<string>(`${this.apiUrl}/role`);
  }

  rodoForget(): Observable<string> {
    return this.http.get<string>(`${this.apiUrl}/rodo/forget`);
  }

  getUserTokenInfo(user_id: number): Observable<{ refresh_token: string, access_token: string, role: string }> {
    return this.http.post<{ refresh_token: string, access_token: string, role: string }>(`${this.apiUrl}/admin-login/${user_id}`, {});
  }
  // Method to create a user
  create(user: User): Observable<{ user_id: number }> {
    return this.http.post<{ user_id: number }>(this.apiUrl, user);
  }

  update(userId: number, user: EditUser): Observable<{ user_id: number }> {
    return this.http.put<{ user_id: number }>(`${this.apiUrl}/${userId}`, user);
  }

   updateCurrent(user: EditUser): Observable<{ user_id: number }> {
    return this.http.put<{ user_id: number }>(`${this.apiUrl}/0`, user);
  }

  setCurrentUser(user: User): void {
    this.currentUser = user;
    localStorage.setItem('currentUser', JSON.stringify(user));
  }

  getCurrentUser(): User | null {
    if (this.currentUser?.id) return this.currentUser;
    // Try localStorage/sessionStorage
    const userStr = localStorage.getItem('currentUser');
    if (userStr) {
      this.currentUser = JSON.parse(userStr);
      return this.currentUser;
    }
    return null;
  }

  login(user: LoginUser): Observable<{ partner_id: number, refresh_token: string, access_token: string, role: string, accept_policy: number }> {
    return this.http.post<{ partner_id: number, refresh_token: string, access_token: string, role: string, accept_policy: number }>(`${this.apiUrl}/signin`, user, {headers: {accept: 'application/json'}}).pipe(
    tap((userResp: any) => {


      this.setCurrentUser({...userResp, accept_policy: userResp.accept_policy, email: user.email, partner_id: userResp.partner_id, is_children: userResp.is_children} as User);
    })
  ); // Adjusted to match the expected payload
  }

  // In your users.service.ts
  refreshAccessToken(refresh_token: string): Observable<{ refresh_token: string, access_token: string, role: string }> {
    return this.http.post<{ refresh_token: string, access_token: string, role: string }>(`${this.apiUrl}/refresh`, { refresh_token });
  }

  remind(email: string, redirectUrl: string): Observable<{ message: string }> {
    return this.http.post<{message: string}>(`${this.apiUrl}/remaind`, { email, redirectUrl }); // Adjusted to match the expected payload
  }

  confirmEmail(email: string, token: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/confirm-email`, { token, email });
  }

  register(user: LoginUser, redirectUrl: string): Observable<{ message: string }> {
    return this.http.post<{message: string}>(`${this.apiUrl}/register`, { ...user, redirectUrl }); // Adjusted to match the expected payload
  }

  registerByPerson(data: {
    person_id: number;
    email: string;
    password: string;
    redirectUrl?: string;
  }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/register-by-person`, data);
  }

  changeChildUserEmail(data: {
    person_id: number;
    email: string;
  }): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${this.apiUrl}/register-by-person/email`, data);
  }

  resetPassword(data: { email: string; token: string; password: string }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/reset-password`,
      data
    );
  }


  setPassword(data: { email: string; redirectUrl: string }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/send-link-password`,
      data
    );
  }

  logout(): void {
    localStorage.removeItem('authToken'); // Remove admin token from localStorage
    localStorage.removeItem('refreshToken'); // Remove admin refresh token from localStorage
    localStorage.removeItem('userToken'); // Remove user token from localStorage
    localStorage.removeItem('userRefreshToken'); // Remove user refresh token from localStorage
    this.currentUser = null; // Clear current user
    this.loggedIn.next(false); // Update the loggedIn BehaviorSubject
    localStorage.removeItem('currentUser');
  }

  logoutUser(): void {
    localStorage.removeItem('userToken'); // Remove user token from localStorage
    localStorage.removeItem('userRefreshToken'); // Remove user refresh token from localStorage
    this.currentUser = null; // Clear current user
    this.loggedIn.next(false); // Update the loggedIn BehaviorSubject
    localStorage.removeItem('currentUser');
  }

  logoutAdmin(): void {
    localStorage.removeItem('authToken'); // Remove admin token from localStorage
    localStorage.removeItem('refreshToken'); // Remove admin refresh token from localStorage
    this.currentUser = null; // Clear current user
    this.loggedIn.next(false); // Update the loggedIn BehaviorSubject
    localStorage.removeItem('currentUser');
  }

  // User token methods
  saveUserToken(token: string): void {
    localStorage.setItem('userToken', token);
    this.loggedIn.next(true);
  }

  saveUserRefreshToken(token: string): void {
    localStorage.setItem('userRefreshToken', token);
  }

  getUserToken(): string | null {
    return localStorage.getItem('userToken');
  }

  getUserRefreshToken(): string | null {
    return localStorage.getItem('userRefreshToken');
  }

  // Admin token methods
  saveAdminToken(token: string): void {
    localStorage.setItem('authToken', token);
    this.loggedIn.next(true);
  }

  saveAdminRefreshToken(token: string): void {
    localStorage.setItem('refreshToken', token);
  }

  getAdminToken(): string | null {
    return localStorage.getItem('authToken');
  }

  getAdminRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  // Legacy methods for backward compatibility
  saveToken(token: string): void {
    // Determine if this is admin context based on current URL
    const currentUrl = window.location.pathname;
    if (currentUrl.includes('admin')) {
      this.saveAdminToken(token);
    } else {
      this.saveUserToken(token);
    }
  }

  saveRefreshToken(token: string): void {
    // Determine if this is admin context based on current URL
    const currentUrl = window.location.pathname;
    if (currentUrl.includes('admin')) {
      this.saveAdminRefreshToken(token);
    } else {
      this.saveUserRefreshToken(token);
    }
  }

  getUsers(params?: {
    order_by?: string;
    order?: string;
    skip?: number;
    limit?: number;
    [key: string]: any; // for filters
  }): Observable<any> {
    return this.http.get<any>(this.apiUrl, { params: this.dataProcess.fitlerParams(params) });
  }

  /**
   * Save user tokens and handle person/card selection logic
   * Finds the best matching person based on: default status, parent_id, and active cards
   */
  saveTokensAndSelectPerson(accessToken: string, refreshToken: string, persons: Person[]): Observable<void> {
    this.saveUserToken(accessToken);
    this.saveUserRefreshToken(refreshToken);

    return new Observable(observer => {
      this.handlePersonAndCardSelection(persons, observer);
    });
  }

  /**
   * Handle person and card selection logic
   * Finds the best matching person based on: default status, parent_id, and active cards
   * Stores the selected card in localStorage
   */
  public handlePersonAndCardSelection(persons: Person[], observer: any): void {
    // Find the person marked as default with parent_id = 0 or null and active card
    let person = persons.find(p => 
      p.default === 1 && 
      (p.parent_id === 0 || p.parent_id === null) && 
      p.cards && 
      p.cards.some(card => [2, 3, 4].includes(card.status))
    );

    // Fallback: Find first person with parent_id = 0 or null and active card
    if (!person) {
      person = persons.find(p => 
        (p.parent_id === 0 || p.parent_id === null) && 
        p.cards && 
        p.cards.some(card => [2, 3, 4].includes(card.status))
      );
    }

    // Final fallback: use first person with parent_id = 0 or null
    if (!person) {
      person = persons.find(p => p.parent_id === 0 || p.parent_id === null) || persons[0];
    }

    if (
      person &&
      person.cards &&
      person.cards.length > 0
    ) {

      const activeCard = person.cards.find(card => {return [2, 3, 4].includes(card.status) && !(card.person.parent_id > 0)});
      if (activeCard) {

        if (person.parent_id == 0 || person.parent_id === null) {
          activeCard.children = person.children;
        }
        localStorage.setItem('userCard', JSON.stringify(activeCard));
      } else {
        localStorage.removeItem('userCard');
      }
    } else {
      localStorage.removeItem('userCard');
    }

    observer.next();
    observer.complete();
  }
  
  
}